import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type {
  MarkupStore,
  MarkupConfig,
  FeedbackItem,
  Annotation,
  Comment,
  AnnotationType,
} from '../types';

const defaultConfig: MarkupConfig = {
  projectId: 'default',
  primaryColor: '#070c0f',
  defaultOpen: false,
  shortcut: 'ctrl+shift+m',
  labels: {
    feedbackTitle: 'Report an Issue',
    submitButton: 'Submit Feedback',
    cancelButton: 'Cancel',
  },
  features: {
    screenshot: true,
    annotations: true,
    console: true,
    network: false,
    localStorage: true,
  },
  availableTags: ['Bug', 'Design', 'Content', 'Feature Request', 'Question'],
};

const initialState: Omit<MarkupStore, keyof import('../types').MarkupActions> = {
  isVisible: false,
  isOpen: false,
  isCapturing: false,
  isSteppingAside: false,
  isAnnotating: false,
  currentScreenshot: null,
  annotations: [],
  feedbackItems: [],
  selectedFeedback: null,
  activeTab: 'list',
  activeTool: null,
  toolColor: '#ef4444',
  config: defaultConfig,
  currentUser: null,
  isAuthenticated: false,
  viewportMode: null,
  showPins: true,
  statusFilter: 'all',
  devExport: false,
  authNotice: null,
  isPicking: false,
};

export const useMarkupStore = create<MarkupStore>()(
  persist(
    (set) => ({
      ...initialState,

      setIsVisible: (isVisible: boolean) =>
        set(isVisible ? { isVisible } : { isVisible, isOpen: false }),

      setIsOpen: (isOpen: boolean) => set({ isOpen }),

      setIsCapturing: (isCapturing: boolean) => set({ isCapturing }),

      setIsSteppingAside: (isSteppingAside: boolean) => set({ isSteppingAside }),

      setIsAnnotating: (isAnnotating: boolean) => set({ isAnnotating }),

      setCurrentScreenshot: (screenshot: string | null) =>
        set({ currentScreenshot: screenshot }),

      setAnnotations: (annotations: Annotation[]) =>
        set({ annotations }),

      addAnnotation: (annotation: Annotation) =>
        set((state) => ({
          annotations: [...state.annotations, annotation],
        })),

      updateAnnotation: (id: string, updates: Partial<Annotation>) =>
        set((state) => ({
          annotations: state.annotations.map((a) =>
            a.id === id ? { ...a, ...updates } : a
          ),
        })),

      removeAnnotation: (id: string) =>
        set((state) => ({
          annotations: state.annotations.filter((a) => a.id !== id),
        })),

      clearAnnotations: () => set({ annotations: [] }),

      addFeedbackItem: (item: FeedbackItem) =>
        set((state) => ({
          feedbackItems: [item, ...state.feedbackItems],
        })),

      setFeedbackItems: (items: FeedbackItem[]) =>
        set({ feedbackItems: items }),

      updateFeedbackItem: (id: string, updates: Partial<FeedbackItem>) =>
        set((state) => ({
          feedbackItems: state.feedbackItems.map((f) =>
            f.id === id ? { ...f, ...updates, updatedAt: new Date().toISOString() } : f
          ),
        })),

      updateFeedback: (item: FeedbackItem) =>
        set((state) => ({
          feedbackItems: state.feedbackItems.map((f) =>
            f.id === item.id ? { ...item, updatedAt: new Date().toISOString() } : f
          ),
        })),

      removeFeedbackItem: (id: string) =>
        set((state) => ({
          feedbackItems: state.feedbackItems.filter((f) => f.id !== id),
        })),

      setSelectedFeedback: (feedback: FeedbackItem | null) =>
        set({ selectedFeedback: feedback }),

      setActiveTab: (tab: 'create' | 'list' | 'settings' | 'auth') => set({ activeTab: tab }),

      setActiveTool: (tool: AnnotationType | null) => set({ activeTool: tool }),

      setToolColor: (color: string) => set({ toolColor: color }),

      setConfig: (config: Partial<MarkupConfig>) =>
        set((state) => ({
          config: { ...state.config, ...config },
        })),

      setCurrentUser: (user: import('../types').UserInfo | null) =>
        set({ currentUser: user }),

      setIsAuthenticated: (isAuthenticated: boolean) => set({ isAuthenticated }),

      setAuthNotice: (authNotice: string | null) => set({ authNotice }),

      setIsPicking: (isPicking: boolean) => set({ isPicking }),

      setViewportMode: (mode: { width: number; height: number; deviceType: import('../types').DeviceType; name: string } | null) =>
        set({ viewportMode: mode }),

      addComment: (feedbackId: string, comment: Comment) =>
        set((state) => ({
          feedbackItems: state.feedbackItems.map((f) =>
            f.id === feedbackId
              ? { ...f, comments: [...f.comments, comment], updatedAt: new Date().toISOString() }
              : f
          ),
        })),

      reset: () =>
        set({
          isOpen: false,
          isCapturing: false,
          isSteppingAside: false,
          isAnnotating: false,
          currentScreenshot: null,
          annotations: [],
          selectedFeedback: null,
          activeTab: 'create',
          activeTool: null,
        }),

      setShowPins: (show: boolean) => set({ showPins: show }),

      setStatusFilter: (filter: import('../types').FeedbackStatus | 'all') =>
        set({ statusFilter: filter }),

      setDevExport: (enabled: boolean) => set({ devExport: enabled }),
    }),
    {
      name: 'markup-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        // Don't persist feedbackItems - they come from Firestore
        config: state.config,
        currentUser: state.currentUser,
        isAuthenticated: state.isAuthenticated,
        showPins: state.showPins,
        // Deliberately persisted: the hidden export stays on for whoever
        // enabled it. statusFilter is not — every session starts back on the
        // default view with closed tickets hidden.
        devExport: state.devExport,
      }),
    }
  )
);

export default useMarkupStore;
